import { motion } from "framer-motion";
import type { Category, Factor } from "../data/schema";
import type { ActBreakdown } from "../lib/calc";
import { scientific } from "../lib/format";
import FactorCard from "./FactorCard";
import AnimatedNumber from "./AnimatedNumber";

const ROMAN = ["I", "II", "III", "IV", "V", "VI", "VII", "VIII"];

/**
 * One act of the descent: a category's worth of filters, framed by the running
 * total as the chain ENTERS the act and what survives when it LEAVES. As the
 * reader scrolls act to act, they watch the number step down.
 */
export default function Act({
  index,
  category,
  factors,
  breakdown,
}: {
  index: number;
  category: Category;
  factors: Factor[];
  breakdown: ActBreakdown;
}) {
  return (
    <section className="py-14">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.5 }}
      >
        <p className="text-xs font-medium uppercase tracking-[0.3em] text-cyan/80">
          Act {ROMAN[index] ?? index + 1}
        </p>
        <h2 className="mt-2 font-display text-3xl font-light text-white sm:text-4xl">
          {category.label}
        </h2>
        {category.blurb && (
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-white/70 sm:text-base">
            {category.blurb}
          </p>
        )}

        <p className="mt-6 text-sm text-white/55">
          Entering with{" "}
          <span className="font-mono text-white/80">
            {scientific(breakdown.enter)}
          </span>{" "}
          planets still in the running.
        </p>
      </motion.div>

      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        {factors.map((factor) => (
          <FactorCard key={factor.id} factor={factor} />
        ))}
      </div>

      {/* The step-down: what this whole act did to the number. */}
      <div className="mt-8 rounded-2xl border border-cyan/20 bg-cyan/[0.04] p-5 text-center">
        <p className="text-[11px] uppercase tracking-[0.2em] text-white/55">
          This act multiplied the count by
        </p>
        <p className="mt-1 font-mono text-xl text-cyan">
          × {scientific(breakdown.multiplier)}
        </p>
        <div className="mx-auto my-3 h-px w-16 bg-white/15" />
        <p className="text-[11px] uppercase tracking-[0.2em] text-white/55">
          Planets still standing
        </p>
        <AnimatedNumber
          value={breakdown.exit}
          format={(n) => scientific(n)}
          className="mt-1 block font-display text-3xl font-light text-white sm:text-4xl"
        />
      </div>
    </section>
  );
}
